import { Link } from "react-router-dom";

const LIME = "#D4FF4A";
const INK = "#030305";

const variants = [
  {
    id: 1,
    name: "Crosshair",
    pitch: "Literal target. Reads as 'find the buyer.' Crosshair scales down to favicon cleanly.",
    Mark: ({ size = 28 }) => (
      <div
        className="rounded-md flex items-center justify-center"
        style={{ width: size, height: size, background: LIME }}
      >
        <svg width={size * 0.7} height={size * 0.7} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke={INK} strokeWidth="1.6" />
          <circle cx="12" cy="12" r="4.5" stroke={INK} strokeWidth="1.6" />
          <circle cx="12" cy="12" r="1.5" fill={INK} />
          <line x1="12" y1="0.5" x2="12" y2="3.5" stroke={INK} strokeWidth="1.6" />
          <line x1="12" y1="20.5" x2="12" y2="23.5" stroke={INK} strokeWidth="1.6" />
          <line x1="0.5" y1="12" x2="3.5" y2="12" stroke={INK} strokeWidth="1.6" />
          <line x1="20.5" y1="12" x2="23.5" y2="12" stroke={INK} strokeWidth="1.6" />
        </svg>
      </div>
    ),
  },
  {
    id: 2,
    name: "Pixel grid",
    pitch: "4×4 grid with one lit cell = the 7% who matter. Quietly nerdy. Reads at any size.",
    Mark: ({ size = 28 }) => (
      <div
        className="rounded-md flex items-center justify-center"
        style={{ width: size, height: size, background: LIME }}
      >
        <svg width={size * 0.66} height={size * 0.66} viewBox="0 0 16 16" fill="none">
          {Array.from({ length: 16 }).map((_, i) => {
            const x = (i % 4) * 4;
            const y = Math.floor(i / 4) * 4;
            const lit = i === 5;
            return (
              <rect
                key={i}
                x={x}
                y={y}
                width="3"
                height="3"
                rx="0.5"
                fill={lit ? INK : "transparent"}
                stroke={INK}
                strokeWidth={lit ? 0 : 0.7}
                opacity={lit ? 1 : 0.45}
              />
            );
          })}
        </svg>
      </div>
    ),
  },
  {
    id: 3,
    name: "Signal beacon",
    pitch: "Dot with concentric arcs = intel/signal. Feels alive, intelligence-coded.",
    Mark: ({ size = 28 }) => (
      <div
        className="rounded-md flex items-center justify-center"
        style={{ width: size, height: size, background: LIME }}
      >
        <svg width={size * 0.72} height={size * 0.72} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="14" r="2.4" fill={INK} />
          <path d="M6.5 11.5 A 7 7 0 0 1 17.5 11.5" stroke={INK} strokeWidth="1.6" strokeLinecap="round" fill="none" />
          <path d="M3 10 A 11.5 11.5 0 0 1 21 10" stroke={INK} strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.55" />
        </svg>
      </div>
    ),
  },
  {
    id: 4,
    name: "Bracket I",
    pitch: "Code/data brackets framing the I. Reads engineered, dev-tool aesthetic. Closest to current.",
    Mark: ({ size = 28 }) => (
      <div
        className="rounded-md flex items-center justify-center"
        style={{ width: size, height: size, background: LIME }}
      >
        <svg width={size * 0.7} height={size * 0.7} viewBox="0 0 24 24" fill="none">
          <path d="M5.5 3 H 3 V 21 H 5.5" stroke={INK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <path d="M18.5 3 H 21 V 21 H 18.5" stroke={INK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <rect x="11" y="6" width="2" height="12" rx="0.5" fill={INK} />
        </svg>
      </div>
    ),
  },
  {
    id: 5,
    name: "Distribution curve",
    pitch: "Bell curve with a marked outlier on the right tail = the high-value buyer. Strategic, mathy.",
    Mark: ({ size = 28 }) => (
      <div
        className="rounded-md flex items-center justify-center"
        style={{ width: size, height: size, background: LIME }}
      >
        <svg width={size * 0.78} height={size * 0.78} viewBox="0 0 28 24" fill="none">
          <path
            d="M2 20 C 6 20, 8 6, 14 6 C 20 6, 22 20, 26 20"
            stroke={INK}
            strokeWidth="1.8"
            strokeLinecap="round"
            fill="none"
          />
          <line x1="2" y1="20.5" x2="26" y2="20.5" stroke={INK} strokeWidth="1.2" opacity="0.5" />
          <circle cx="22.5" cy="14.5" r="2.2" fill={INK} />
        </svg>
      </div>
    ),
  },
];

export default function LogoLab() {
  return (
    <div className="min-h-screen bg-[#030305] text-white">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16">
        <Link to="/" className="font-mono text-xs text-[#71717A] hover:text-white">
          ← back to site
        </Link>

        <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-6 mb-3 tracking-tight">
          Logo lab — 5 directions
        </h1>
        <p className="text-[#A1A1AA] text-base max-w-[640px] mb-12">
          Each variant shown at three sizes (favicon 16px, header 28px, hero 56px) on dark and
          light backgrounds. Pick a number and I'll wire it into the header + generate the favicon.
        </p>

        <div className="space-y-12">
          {variants.map((v) => (
            <Row key={v.id} v={v} />
          ))}
        </div>

        <div className="mt-20 pt-10 border-t border-white/[0.06] text-[#A1A1AA] text-sm">
          Current logo (for reference) is variant 4-ish — solid lime square with a bold serif "I".
          The new options trade the generic "I" for marks that say something about the product.
        </div>
      </div>
    </div>
  );
}

function Row({ v }) {
  const { Mark } = v;
  return (
    <section className="border border-white/[0.06] rounded-2xl overflow-hidden bg-[#0A0A0F]">
      <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="font-mono text-[#D4FF4A]/70 text-[11px]">VARIANT 0{v.id}</span>
          <span className="font-display text-white text-[18px] font-semibold">{v.name}</span>
        </div>
        <span className="font-mono text-[#52525B] text-[10px]">click to copy: variant {v.id}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="p-8 bg-[#030305] border-b md:border-b-0 md:border-r border-white/[0.06]">
          <div className="font-mono-label text-[#71717A] text-[10px] mb-5">on dark</div>
          <div className="flex items-end gap-10 mb-6">
            <SizeStack Mark={Mark} sizes={[16, 28, 56]} />
          </div>
          <Lockup Mark={Mark} dark />
        </div>

        <div className="p-8 bg-[#F5F5F4]">
          <div className="font-mono-label text-[#71717A] text-[10px] mb-5">on light</div>
          <div className="flex items-end gap-10 mb-6">
            <SizeStack Mark={Mark} sizes={[16, 28, 56]} />
          </div>
          <Lockup Mark={Mark} dark={false} />
        </div>
      </div>

      <div className="px-6 py-4 border-t border-white/[0.06] text-[#A1A1AA] text-[13px] leading-[1.55]">
        <span className="text-white font-medium">Pitch:</span> {v.pitch}
      </div>
    </section>
  );
}

function SizeStack({ Mark, sizes }) {
  return (
    <>
      {sizes.map((s) => (
        <div key={s} className="flex flex-col items-center gap-2">
          <Mark size={s} />
          <span className="font-mono text-[10px] text-[#71717A]">{s}px</span>
        </div>
      ))}
    </>
  );
}

function Lockup({ Mark, dark }) {
  return (
    <div className="flex items-center gap-2">
      <Mark size={28} />
      <span
        className={`font-display font-semibold text-[17px] tracking-tight ${
          dark ? "text-white" : "text-[#030305]"
        }`}
      >
        ICPIntel
      </span>
    </div>
  );
}
